export interface ImageCropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = src;
  });

export async function cropImageToBlob(
  imageSrc: string,
  crop: ImageCropArea,
  outputSize = 512,
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp' = 'image/jpeg',
  quality = 0.92
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas is unavailable');
  }

  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to export cropped image'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

export function clampCropPan(params: {
  x: number;
  y: number;
  viewportSize: number;
  displayWidth: number;
  displayHeight: number;
}): { x: number; y: number } {
  const minX = params.viewportSize - params.displayWidth;
  const minY = params.viewportSize - params.displayHeight;
  return {
    x: Math.min(0, Math.max(minX, params.x)),
    y: Math.min(0, Math.max(minY, params.y)),
  };
}

export function cropAreaFromPan(params: {
  panX: number;
  panY: number;
  viewportSize: number;
  scale: number;
}): ImageCropArea {
  const size = params.viewportSize / params.scale;
  return {
    x: -params.panX / params.scale,
    y: -params.panY / params.scale,
    width: size,
    height: size,
  };
}

export function baseCoverScale(
  viewportSize: number,
  naturalWidth: number,
  naturalHeight: number
): number {
  if (naturalWidth <= 0 || naturalHeight <= 0) return 1;
  return Math.max(viewportSize / naturalWidth, viewportSize / naturalHeight);
}
